/**
 * Discovery / NoDbAccess - evidence-mode catalogue + schema-upload wizard.
 *
 * Two surfaces in one page:
 *
 *   1. **Evidence-source catalogue** (GET /v1/discovery/evidence-sources)
 *      Shows source types where Discovery is classifying PII but does not
 *      have direct query access - typically M365 / Google Workspace / log
 *      shippers / Kafka streams. Each row shows the evidence-collection
 *      mode (graph_api vs direct_query).
 *
 *   2. **Schema-upload wizard** (POST /v1/discovery/evidence/schema-export)
 *      Customer DBA pastes/uploads schema-only DDL (pg_dump --schema-only,
 *      MSSQL Generate Scripts, Oracle DBMS_METADATA). The backend parses
 *      CREATE TABLE statements, classifies column NAMES against the
 *      Indian-context PII vocabulary, and persists findings - never
 *      touches the live DB. Unblocks BFSI deals where the DBA shares
 *      schemas but never credentials. Phase 0 Day 3 (2026-05-22).
 *
 *   3. **Mobile app upload** (POST /v1/discovery/evidence/mobile-app, bank
 *      I-24): an APK or IPA scanned offline for tracker SDKs and sensitive
 *      permissions. See MobileAppUploadCard.tsx.
 */
import { Rx, useRegulationText } from '@/lib/regulation-terms';
import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { ShieldOff, Cloud, Database, Loader2, AlertTriangle, Upload, FileText, CheckCircle2, ArrowRight, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import {
  listEvidenceSources,
  ingestSchemaExport,
  listBusinessUnits,
  type BusinessUnit,
  type EvidenceSourceRow,
  type SchemaExportResponse,
} from '@/lib/api';
import { AnalysedMobileApps } from './AnalysedMobileApps';
import { MobileAppUploadCard } from './MobileAppUploadCard';

/** Max DDL size - backend caps at 1 MB; we mirror that here to surface a UX hint early. */
const MAX_DDL_BYTES = 1024 * 1024;

export function DiscoveryNoDbAccess() {
  const tx = useRegulationText();
  const [sources, setSources] = useState<EvidenceSourceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /** Schema-upload wizard state (Day 3). */
  const [showWizard, setShowWizard] = useState(false);
  const [assetExternalRef, setAssetExternalRef] = useState('');
  const [uploadLabel, setUploadLabel] = useState('');
  const [ddl, setDdl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [wizardError, setWizardError] = useState<string | null>(null);
  const [wizardResult, setWizardResult] = useState<SchemaExportResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState('');
  /** '' = let the server decide (own unit, or tenant-wide for a user who sees every unit). */
  const [businessUnitId, setBusinessUnitId] = useState('');
  /** Units this user can see (RLS-scoped server-side); empty hides the picker. */
  const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>([]);
  const [showMobile, setShowMobile] = useState(false);

  const refresh = (signal?: AbortSignal) =>
    listEvidenceSources()
      .then((r) => !signal?.aborted && setSources(r.sources))
      .catch((err) => !signal?.aborted && setError(err instanceof Error ? err.message : 'load failed'))
      .finally(() => !signal?.aborted && setLoading(false));

  useEffect(() => {
    const ctrl = new AbortController();
    refresh(ctrl.signal);
    return () => ctrl.abort();
  }, []);

  // The unit picker for the upload. The list is already limited to the units
  // this user can see; a user without access to the list simply gets no
  // picker, and the server applies its default.
  useEffect(() => {
    if (!showWizard && !showMobile) return;
    let live = true;
    listBusinessUnits({ status: 'active' })
      .then((r) => live && setBusinessUnits(r.businessUnits))
      .catch(() => live && setBusinessUnits([]));
    return () => {
      live = false;
    };
  }, [showWizard, showMobile]);

  function resetWizard() {
    setAssetExternalRef('');
    setUploadLabel('');
    setDdl('');
    setFileName('');
    setBusinessUnitId('');
    setWizardError(null);
    setWizardResult(null);
    setSubmitting(false);
  }

  async function onFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_DDL_BYTES) {
      setWizardError(`File too large (${(file.size / 1024).toFixed(0)} KB) - max 1 MB.`);
      return;
    }
    setWizardError(null);
    const text = await file.text();
    setDdl(text);
    setFileName(file.name);
    if (!uploadLabel) setUploadLabel(file.name);
  }

  async function onSubmitWizard(e: React.FormEvent) {
    e.preventDefault();
    if (!assetExternalRef.trim() || !ddl.trim()) return;
    setSubmitting(true);
    setWizardError(null);
    setWizardResult(null);
    try {
      const res = await ingestSchemaExport({
        assetExternalRef: assetExternalRef.trim(),
        ddl,
        uploadLabel: uploadLabel.trim() || undefined,
        fileName: fileName || undefined,
        businessUnitId: businessUnitId || undefined,
      });
      setWizardResult(res);
      // Refresh evidence sources so the schema_export source row updates.
      void refresh();
    } catch (err) {
      setWizardError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  }

  const noDbAccess = sources.filter((s) => s.evidenceMode !== 'direct_query');
  const directQuery = sources.filter((s) => s.evidenceMode === 'direct_query');
  const totalAssets = sources.reduce((sum, s) => sum + s.assetCount, 0);

  return (
    <PageContainer>
      <PageHeader
        title="Scan without credentials"
        subtitle={tx("Classify PII in sources Discovery can’t directly query (M365 / Workspace / log streams) - using graph APIs, lineage inference, or sidecar evidence.")}
        badges={[
          { label: `${sources.length} source types`, variant: 'glass' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Load error', variant: 'warning' as const }]
              : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={showMobile ? 'glass' : 'default'}
              onClick={() => setShowMobile((v) => !v)}
            >
              <Smartphone className="h-4 w-4" /> {showMobile ? 'Close app scan' : 'Scan mobile app'}
            </Button>
            <Button
              size="sm"
              variant={showWizard ? 'glass' : 'default'}
              onClick={() => {
                if (showWizard) resetWizard();
                setShowWizard((v) => !v);
              }}
            >
              <Upload className="h-4 w-4" /> {showWizard ? 'Close upload' : 'Upload schema (DDL)'}
            </Button>
          </div>
        }
      />

      {showMobile && <MobileAppUploadCard businessUnits={businessUnits} />}
      {showMobile && <AnalysedMobileApps />}

      {showWizard && (
        <GlassCard className="p-5 mb-6 border-primary/30">
          <CardHeader className="p-0 mb-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-4 w-4 text-primary" /> Upload schema-only DDL
            </CardTitle>
            <CardDescription className="text-xs">
              Paste or upload a <code className="font-mono">pg_dump --schema-only</code>,
              MSSQL <code className="font-mono">Generate Scripts</code>, or Oracle
              <code className="font-mono"> DBMS_METADATA</code> export. Discovery parses the
              <code className="font-mono"> CREATE TABLE</code> statements, classifies column
              names against the Indian-context <Rx>PII</Rx> vocabulary, and persists findings - Discovery
              never connects to your database. Best for BFSI deals where the DBA shares the
              schema but not credentials.
            </CardDescription>
          </CardHeader>

          {wizardResult ? (
            <div className="space-y-4">
              <div className="glass-lite rounded-md p-4 border border-success/40">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-success shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-medium text-sm">Schema ingested</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Asset: <code className="font-mono">{wizardResult.assetExternalRef}</code>
                      {wizardResult.message ? ` · ${wizardResult.message}` : ''}
                    </div>
                    <UploadPlacement result={wizardResult} units={businessUnits} />
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                  <div className="glass-lite rounded p-2 text-center">
                    <div className="text-lg font-semibold tabular">{wizardResult.tablesParsed.toLocaleString('en-IN')}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Tables parsed</div>
                  </div>
                  <div className="glass-lite rounded p-2 text-center">
                    <div className="text-lg font-semibold tabular">{wizardResult.columnsParsed.toLocaleString('en-IN')}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Columns parsed</div>
                  </div>
                  <div className="glass-lite rounded p-2 text-center">
                    <div className="text-lg font-semibold tabular text-success">{wizardResult.accepted.toLocaleString('en-IN')}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Findings accepted</div>
                  </div>
                  <div className="glass-lite rounded p-2 text-center">
                    <div className="text-lg font-semibold tabular">
                      {(wizardResult.rejected ?? 0).toLocaleString('en-IN')}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Rejected</div>
                  </div>
                </div>
                {wizardResult.rejections && wizardResult.rejections.length > 0 && (
                  <details className="mt-3 text-xs">
                    <summary className="cursor-pointer text-muted-foreground">
                      {wizardResult.rejections.length} rejection {wizardResult.rejections.length === 1 ? 'reason' : 'reasons'}
                    </summary>
                    <ul className="mt-2 space-y-1 ml-4 list-disc">
                      {wizardResult.rejections.map((r, i) => (
                        <li key={i}>
                          <code className="text-[10px]">{r.findingType ?? 'unknown'}</code>: {r.reason}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {wizardResult.accepted > 0 && (
                  <Button asChild size="sm">
                    <Link
                      to="/dspm/findings"
                      search={{ asset: wizardResult.assetExternalRef } as never}
                    >
                      View findings <ArrowRight className="h-3 w-3" />
                    </Link>
                  </Button>
                )}
                <Button size="sm" variant="glass" onClick={resetWizard}>
                  Upload another schema
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmitWizard} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="asset-external-ref" className="text-xs font-medium block mb-1">
                    Asset external reference <span className="text-sev-high">*</span>
                  </label>
                  <input
                    id="asset-external-ref"
                    type="text"
                    value={assetExternalRef}
                    onChange={(e) => setAssetExternalRef(e.target.value)}
                    placeholder="e.g. oracle://PROD/CRM"
                    required
                    maxLength={500}
                    className="glass-lite rounded-md px-3 py-2 text-sm w-full outline-none focus:border-primary/40 border border-border/40 font-mono"
                  />
                  <div className="text-[10px] text-muted-foreground mt-1">
                    Names the table or database this schema describes. Re-uploading the same reference into the
                    same business unit updates that upload. Never include a user name and password.
                  </div>
                </div>
                <div>
                  <label htmlFor="upload-label" className="text-xs font-medium block mb-1">
                    Upload label <span className="text-muted-foreground">(optional)</span>
                  </label>
                  <input
                    id="upload-label"
                    type="text"
                    value={uploadLabel}
                    onChange={(e) => setUploadLabel(e.target.value)}
                    placeholder="e.g. CRM 2026-Q2 schema"
                    maxLength={200}
                    className="glass-lite rounded-md px-3 py-2 text-sm w-full outline-none focus:border-primary/40 border border-border/40"
                  />
                </div>
              </div>

              {businessUnits.length > 0 && (
                <div>
                  <label htmlFor="upload-business-unit" className="text-xs font-medium block mb-1">
                    Business unit <span className="text-muted-foreground">(who can see these findings)</span>
                  </label>
                  <select
                    id="upload-business-unit"
                    value={businessUnitId}
                    onChange={(e) => setBusinessUnitId(e.target.value)}
                    className="glass-lite rounded-md px-3 py-2 text-sm w-full outline-none focus:border-primary/40 border border-border/40"
                  >
                    <option value="">Default - your own unit, or the whole workspace if you see every unit</option>
                    {businessUnits.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                  <UploadUnitHint selectedUnitId={businessUnitId} units={businessUnits} />
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="ddl-textarea" className="text-xs font-medium">
                    DDL (schema-only) <span className="text-sev-high">*</span>
                  </label>
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                    <span className="tabular">
                      {(new Blob([ddl]).size / 1024).toFixed(1)} KB / 1024 KB
                    </span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".sql,.ddl,.txt"
                      onChange={onFilePicked}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="glass"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-3 w-3" /> Load .sql
                    </Button>
                  </div>
                </div>
                <textarea
                  id="ddl-textarea"
                  value={ddl}
                  onChange={(e) => setDdl(e.target.value)}
                  placeholder={`-- Paste CREATE TABLE statements or load a .sql file\nCREATE TABLE customers (\n  id BIGINT PRIMARY KEY,\n  email VARCHAR(255),\n  aadhaar_number CHAR(12),\n  pan_number CHAR(10)\n);`}
                  rows={10}
                  required
                  className="glass-lite rounded-md px-3 py-2 text-xs w-full outline-none focus:border-primary/40 border border-border/40 font-mono resize-y"
                />
                <div className="text-[10px] text-muted-foreground mt-1">
                  Schema only - never paste data rows. Discovery never opens a connection to the source DB.
                </div>
              </div>

              {wizardError && (
                <div className="glass-lite border border-sev-high/40 rounded-md p-3 text-xs flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-sev-high shrink-0 mt-0.5" />
                  <div>{wizardError}</div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Button
                  type="submit"
                  size="sm"
                  disabled={submitting || !assetExternalRef.trim() || !ddl.trim()}
                  data-action="disc.dspm_no_db.a01"
                >
                  {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                  {submitting ? 'Parsing + classifying…' : 'Ingest schema'}
                </Button>
                <Button type="button" size="sm" variant="glass" onClick={resetWizard} disabled={submitting}>
                  Reset
                </Button>
              </div>
            </form>
          )}
        </GlassCard>
      )}

      {loading && (
        <LoadingState caption="Loading evidence sources…" />
      )}
      {error && !loading && (
        <ErrorState
          title="Failed to load evidence sources"
          detail={error}
          onRetry={() => { setError(null); setLoading(true); refresh(); }}
        />
      )}

      {!loading && !error && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KpiCard label="Total source types" value={sources.length} icon={Cloud} />
            <KpiCard label="No-DB-Access mode" value={noDbAccess.length} icon={ShieldOff} hint="graph API / sidecar" />
            <KpiCard label="Direct query" value={directQuery.length} icon={Database} hint="standard SQL/HTTP" />
            <KpiCard label="Total assets" value={totalAssets} icon={Database} />
          </div>

          {sources.length === 0 ? (
            <div className="glass-lite rounded-md p-8 text-center text-sm text-muted-foreground">
              <ShieldOff className="h-8 w-8 mx-auto mb-3 opacity-40" />
              <div className="font-medium text-foreground/90">No connected sources yet</div>
              <div className="mt-1">Connect a data source from <strong>Connectors</strong> to start populating this view.</div>
            </div>
          ) : (
            <>
              {noDbAccess.length > 0 && (
                <GlassCard className="p-0 overflow-hidden mb-6">
                  <CardHeader className="p-5 pb-3">
                    <CardTitle>No-DB-Access sources</CardTitle>
                    <CardDescription clamp>Sources accessed via graph API / sidecar rather than direct query.</CardDescription>
                  </CardHeader>
                  {noDbAccess.map((s, i) => (
                    <motion.div
                      key={s.sourceType}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.04 }}
                      className="grid grid-cols-12 items-center px-5 py-3 text-sm border-t border-border/30"
                    >
                      <div className="col-span-4">
                        <div className="font-medium">{s.displayName}</div>
                        <code className="text-[10px] text-muted-foreground">{s.sourceType}</code>
                      </div>
                      <div className="col-span-2"><Badge variant="glass" className="text-[10px]">{s.category}</Badge></div>
                      <div className="col-span-2 text-right tabular text-xs">{s.assetCount.toLocaleString('en-IN')} assets</div>
                      <div className="col-span-2 text-right tabular text-xs">{s.findingCount.toLocaleString('en-IN')} findings</div>
                      <div className="col-span-2 text-right">
                        <Badge variant="warning" className="text-[9px]">{s.evidenceMode.replace('_', ' ')}</Badge>
                      </div>
                    </motion.div>
                  ))}
                </GlassCard>
              )}

              {directQuery.length > 0 && (
                <GlassCard className="p-0 overflow-hidden">
                  <CardHeader className="p-5 pb-3">
                    <CardTitle>Direct-query sources</CardTitle>
                    <CardDescription clamp>Sources accessed via standard JDBC / API query.</CardDescription>
                  </CardHeader>
                  {directQuery.map((s, i) => (
                    <motion.div
                      key={s.sourceType}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03 }}
                      className="grid grid-cols-12 items-center px-5 py-3 text-sm border-t border-border/30"
                    >
                      <div className="col-span-4">
                        <div className="font-medium">{s.displayName}</div>
                        <code className="text-[10px] text-muted-foreground">{s.sourceType}</code>
                      </div>
                      <div className="col-span-2"><Badge variant="glass" className="text-[10px]">{s.category}</Badge></div>
                      <div className="col-span-2 text-right tabular text-xs">{s.assetCount.toLocaleString('en-IN')} assets</div>
                      <div className="col-span-2 text-right tabular text-xs">{s.findingCount.toLocaleString('en-IN')} findings</div>
                      <div className="col-span-2 text-right">
                        <Badge variant="success" className="text-[9px]">direct query</Badge>
                      </div>
                    </motion.div>
                  ))}
                </GlassCard>
              )}
            </>
          )}
        </>
      )}
    </PageContainer>
  );
}

/**
 * Under the unit picker, BEFORE the upload is sent: who will see it. A chosen
 * unit keeps it from users limited to other units. The default is the
 * uploader's own unit when their access is limited to one, and tenant-wide
 * for a user who sees every unit - so the default must not promise that other
 * units cannot see it (review of PR #83, F83-6).
 */
export function UploadUnitHint({
  selectedUnitId,
  units,
}: {
  selectedUnitId: string;
  units: ReadonlyArray<Pick<BusinessUnit, 'id' | 'name'>>;
}) {
  const chosen = selectedUnitId ? units.find((u) => u.id === selectedUnitId) : undefined;
  return (
    <div className="text-[10px] text-muted-foreground mt-1" data-testid="upload-unit-hint">
      {selectedUnitId ? (
        <>
          Stored in <span className="font-medium text-foreground">{chosen?.name ?? selectedUnitId}</span>. Users limited
          to other business units will not see these findings.
        </>
      ) : (
        <>
          Default: your own business unit if your access is limited to one. If you can see every unit, the upload is
          tenant-wide and every user in this workspace can see these findings.
        </>
      )}{' '}
      Stored as an operator upload under your name, not as scanner evidence.
    </div>
  );
}

/**
 * Where an upload's findings went and how they are recorded: an operator
 * upload under the signed-in user, in a business unit or tenant-wide. Says it
 * plainly so nobody reads name-inferred findings as scanner evidence, and so a
 * tenant-wide upload is a visible choice rather than a silent default.
 *
 * The unit named here is where the findings are. An upload only ever writes
 * its own upload asset for that unit (the controller keys it by unit, see
 * asset-resolver.ts OPERATOR UPLOAD NAMESPACE); it never lands in a scanned
 * asset or another unit's upload.
 */
export function UploadPlacement({
  result,
  units,
}: {
  result: SchemaExportResponse;
  units: ReadonlyArray<Pick<BusinessUnit, 'id' | 'name'>>;
}) {
  if (result.trustLevel !== 'operator_upload') return null;
  const unitName =
    result.businessUnitId != null
      ? (units.find((u) => u.id === result.businessUnitId)?.name ?? result.businessUnitId)
      : null;
  return (
    <div className="text-xs text-muted-foreground mt-2 space-y-1" data-testid="upload-placement">
      <div>
        Recorded as an <span className="font-medium text-foreground">operator upload</span> under your name - inferred
        from column names, not scanned, no scanner attestation.
      </div>
      <div>
        {unitName !== null ? (
          <>
            Business unit: <span className="font-medium text-foreground">{unitName}</span>. Users limited to other units
            will not see these findings.
          </>
        ) : (
          <>
            <span className="font-medium text-foreground">Tenant-wide</span> (no business unit): visible to every user
            in this workspace, like any other finding without a unit.
          </>
        )}
      </div>
      <div>
        It is kept apart from any scanned asset with the same reference: an upload never changes what a scan found,
        and a scan never lands in an upload. The Inventory lists it as its own asset, labelled uploaded schema.
      </div>
    </div>
  );
}
